// Display-group colour swatches hydrate lazily per card from ?view=display-group-swatches.
// Hovering a swatch previews that sibling product in place from its ?view=card.
// Exposes window.DisplayGroupSwatches.register(root).
(() => {
  const SELECTOR = '[data-display-group-swatches][data-swatch-src]';
  const PREVIEW_SETTLE = 90; // ms of hover before swapping, avoids flicker on drag-through
  const RESET_SETTLE = 120; // ms before restoring, long enough for an adjacent swatch's mouseenter to cancel it
  const PREFETCH_SETTLE = 120; // ms of card hover before warming sibling variants, skips quick fly-overs

  const hydrate = async (slot) => {
    if (slot.dataset.swatchState) return;
    const src = slot.getAttribute('data-swatch-src');
    if (!src) return;
    slot.dataset.swatchState = 'loading';
    try {
      const response = await fetch(src, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(String(response.status));
      const template = document.createElement('template');
      template.innerHTML = (await response.text()).trim();
      const rendered = template.content.firstElementChild;
      if (rendered) {
        slot.replaceWith(rendered);
        wireCardPreview(rendered);
      } else {
        // No swatches: keep the reserved slot so the card height doesn't shift.
        slot.dataset.swatchState = 'empty';
      }
    } catch (_error) {
      slot.dataset.swatchState = 'error';
    }
  };

  const REGIONS = ['.jam-card__media', '.jam-card__title', '.jam-card__price', '.jam-card__features'];
  const htmlCache = new Map();

  const cardUrl = (url) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('view', 'card');
    return u.pathname + u.search;
  };

  const fetchCardHtml = (url) => {
    if (!htmlCache.has(url)) {
      htmlCache.set(
        url,
        fetch(cardUrl(url), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      );
    }
    return htmlCache.get(url);
  };

  const nodeFrom = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  };

  const parseCard = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector('.jam-card');
  };

  // Warm a sibling variant's ?view=card HTML and media images ahead of a hover. Deduped.
  const prefetchedImages = new Set();

  const prefetchImage = (url) => {
    if (!url || prefetchedImages.has(url)) return;
    prefetchedImages.add(url);
    const img = new Image();
    img.decoding = 'async';
    try { img.fetchPriority = 'low'; } catch (_error) { /* not supported on older browsers */ }
    img.src = url;
  };

  const prefetchSiblingCard = (url) =>
    fetchCardHtml(url).then((html) => {
      const card = html ? parseCard(html) : undefined;
      card?.querySelectorAll('.jam-card__media img').forEach((img) => prefetchImage(img.getAttribute('src')));
    });

  const regionsOf = (card) =>
    REGIONS.reduce((acc, sel) => {
      const el = card.querySelector(sel);
      return { ...acc, [sel]: el ? el.outerHTML : undefined };
    }, {});

  // Swap media/title/price in place and toggle features, never touching the live swatch row.
  const applyRegions = (card, regions) => {
    ['.jam-card__media', '.jam-card__title', '.jam-card__price'].forEach((sel) => {
      const existing = card.querySelector(`:scope > ${sel}`);
      if (existing && regions[sel]) existing.replaceWith(nodeFrom(regions[sel]));
    });
    const existingFeatures = card.querySelector(':scope > .jam-card__features');
    if (regions['.jam-card__features']) {
      const node = nodeFrom(regions['.jam-card__features']);
      if (existingFeatures) existingFeatures.replaceWith(node);
      else card.querySelector(':scope > .jam-card__price')?.after(node);
    } else if (existingFeatures) {
      existingFeatures.remove();
    }
  };

  const wireCardPreview = (swatchesEl) => {
    const card = swatchesEl.closest('.jam-card');
    if (!card || card.dataset.previewWired) return;
    card.dataset.previewWired = 'true';

    // A card hover warms every sibling variant. One-shot per card, behind a short settle.
    let prefetched = false;
    let prefetchTimer;
    const prefetchSiblings = () => {
      if (prefetched) return;
      prefetched = true;
      swatchesEl.querySelectorAll('.jam-card__swatch').forEach((swatch) => {
        const url = swatch.getAttribute('href');
        if (url) prefetchSiblingCard(url);
      });
    };
    card.addEventListener('mouseenter', () => { prefetchTimer = setTimeout(prefetchSiblings, PREFETCH_SETTLE); });
    card.addEventListener('mouseleave', () => clearTimeout(prefetchTimer));

    const original = regionsOf(card);
    let settle;
    let restore;
    let activeUrl;

    const preview = async (url) => {
      activeUrl = url;
      const html = await fetchCardHtml(url);
      if (activeUrl !== url || !html) return; // moved on, or fetch failed
      const src = parseCard(html);
      if (src) applyRegions(card, regionsOf(src));
    };

    const reset = () => {
      clearTimeout(settle);
      activeUrl = undefined;
      card.classList.remove('jam-card--previewing');
      applyRegions(card, original);
    };

    swatchesEl.querySelectorAll('.jam-card__swatch').forEach((swatch) => {
      const url = swatch.getAttribute('href');
      if (!url) return;
      swatch.addEventListener('mouseenter', () => {
        clearTimeout(restore); // cancel a pending restore from crossing the gap to get here
        clearTimeout(settle);
        card.classList.add('jam-card--previewing'); // force the primary image, not the hover shot
        fetchCardHtml(url); // start the fetch now, in parallel with the settle delay
        settle = setTimeout(() => preview(url), PREVIEW_SETTLE);
      });
    });

    // mouseleave fires when crossing a gap between swatches too, so defer the restore for an adjacent mouseenter to cancel.
    swatchesEl.addEventListener('mouseleave', () => {
      clearTimeout(restore);
      restore = setTimeout(reset, RESET_SETTLE);
    });
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              obs.unobserve(entry.target);
              hydrate(entry.target);
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  const register = (root) => {
    (root || document).querySelectorAll(SELECTOR).forEach((slot) => {
      if (slot.dataset.swatchObserved) return;
      slot.dataset.swatchObserved = 'true';
      if (observer) {
        observer.observe(slot);
      } else {
        hydrate(slot);
      }
    });
  };

  register();
  document.addEventListener('shopify:section:load', (event) => register(event.target));

  // Auto-hydrate cards injected by any surface without each calling register. Debounced to one scan per frame.
  if ('MutationObserver' in window) {
    let scheduled = false;
    new MutationObserver((mutations) => {
      if (scheduled) return;
      const hasNewSlot = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) => node.nodeType === 1 && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))
        )
      );
      if (!hasNewSlot) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        register(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Surfaces that inject cards after load can also call this explicitly.
  window.DisplayGroupSwatches = { register };
})();
